import {generateTokensFromObject} from '../__utils';
import {coreColorTokens, coreColors} from '../colors';

import {darkDefaultTheme} from './darkDefaultTheme';

import type {ThemeColors} from '../colors';
import type {ThemeObject} from '../theme.types';

/**
 * These values are inlined into <body> styles via jade to prevent flash of
 * unstyled content (FOUC) before the theme stylesheet loads.
 *
 * - `background` — body background. Keep in sync with the `background1` token
 *   in the colors object below.
 * - `loadingTrack` — preloader progress-bar track. Keep in sync with the
 *   `background2` token in the colors object below.
 * - `loadingBackground` — preloader screen background. White in light theme,
 *   black in dark themes. Not tied to a colors-object token.
 * - `loadingBloomOpacity` — opacity for the preloader's glow stack. Subtle on
 *   light backgrounds, full on dark backgrounds.
 * - `loadingText` — preloader text color. Mirrors the `textPrimary`
 *   token so loader copy stays readable on the loader background.
 */
export const DARK_DARKER_HTML_BACKGROUND_COLORS = {
  background: coreColors.d2.gray[1200],
  loadingTrack: coreColors.d2.gray[1100],
  loadingBackground: coreColors.d2.black,
  loadingBloomOpacity: 1,
  loadingText: coreColors.d2.white,
};

const colors: ThemeColors = {
  ...darkDefaultTheme.colors,
  /**
   * Simplifying background colors.
   * The previous background 1 ... 5 need to be consolidated (renaming callsites)
   * to the primary ... tertiary values.
   *
   * @see https://webflow.atlassian.net/browse/DS-761
   */
  backgroundPrimary: coreColorTokens.d2.gray[1200],
  backgroundSecondary: coreColorTokens.d2.gray[1100],
  backgroundTertiary: coreColorTokens.d2.gray[1000],
  background1: coreColorTokens.d2.gray[1200],
  background2: coreColorTokens.d2.gray[1100],
  background3: coreColorTokens.d2.gray[1000],
  uiBorderSolid: '#3b3b3b',
  uiCanvasBackground: coreColorTokens.d2.gray[1000],
  uiDragOverlayBackground: coreColorTokens.d2.gray[1000],
  uiMenuContentBackground: coreColorTokens.d2.gray[1000],
  uiMenuContentBackgroundLight: coreColorTokens.d2.gray[1100], // $background2
  uiPopoverBackdrop: coreColorTokens.d2.blackA60,
};

export const darkDarkerTheme: ThemeObject = {
  ...darkDefaultTheme,
  colors,
};

export const darkDarkerCSSVariables =
  generateTokensFromObject(darkDarkerTheme).setTokens;
